// @section 1 VPG Reply Review — Patrick's one-thread-at-a-time reply queue.
//
// Left: the queue (every GHL thread whose newest message is inbound and unanswered).
// Right: the selected conversation plus the model's candidate replies. Patrick picks one,
// edits one into a fourth option, or rejects all of them, then hits Execute.
//
// Two things this screen must never do, both enforced server-side as well:
//   - auto-send. Execute is the only transmit path and it is always a human click.
//   - reach an opt-out. Those threads are excluded from deals.v_reply_review_queue, and the
//     detail endpoint returns sendable=false, which hides Execute entirely.
//
// The vote is the product. Which variant ships, and whether it shipped unedited, is written
// to deals.reply_decision and becomes the training signal for the reply model.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  listVpgQueue, getVpgThread, getVpgFacets, getVpgScoreboard, listVpgDeliveryIssues,
  executeVpgReply, rejectVpgReply, requestVpgDraft,
  type VpgQueueRow, type VpgThreadDetail, type VpgCandidate,
  type VpgFacets, type VpgScoreRow, type VpgDeliveryIssue,
} from '../api';
import { toast } from '../components/Toast';
import { useIsMobile } from '../lib/useIsMobile';

// @subsection 1.1 Variant presentation
// v5 first: it is the current preferred voice (soft ack then a real qualifying question).
const VARIANT_META: Record<string, { label: string; blurb: string; accent: string }> = {
  v5: { label: 'V5 · blended', accent: '#14C1A8', blurb: 'Soft ack, then a qualifying question that reads the agent' },
  v3: { label: 'V3 · double text', accent: '#d97706', blurb: 'No intro filler, steers to projects and off-market' },
  template: { label: 'Template', accent: '#7c87a8', blurb: 'Verbatim mined company snippet for this persona' },
  brief: { label: 'Brief', accent: '#7c87a8', blurb: 'One line. Bias to brevity.' },
};

const CLASS_TONE: Record<string, string> = {
  escalate: '#f59e0b',
  needs_human: '#f59e0b',
  interested: '#14C1A8',
  not_now: '#7c87a8',
  under_contract: '#7c87a8',
  identity_check: '#60a5fa',
  unclassified: '#7c87a8',
};

// @subsection 1.2 Formatting helpers
function waitLabel(minutes?: number | null): string {
  if (minutes == null) return '—';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const h = minutes / 60;
  if (h < 48) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}

// Anything past 48h is a nurture bump, not an answer to the inbound (the mined model's
// latency gate). Flagging it keeps Patrick from firing a live-conversation reply at a
// thread that went cold a week ago.
function waitTone(minutes?: number | null): string {
  if (minutes == null) return 'var(--tx-3)';
  if (minutes > 48 * 60) return '#f87171';
  if (minutes > 4 * 60) return '#f59e0b';
  return '#14C1A8';
}

function fmtTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const S = {
  pane: { border: '1px solid var(--t2-border)', borderRadius: 6, background: 'rgba(10,19,34,0.35)' } as const,
  label: { fontSize: 12, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--tx-3)', fontWeight: 700 } as const,
};

export default function VpgReplies() {
  const isMobile = useIsMobile();
  const px = isMobile ? 12 : 22;

  const [rows, setRows] = useState<VpgQueueRow[] | null>(null);
  const [facets, setFacets] = useState<VpgFacets | null>(null);
  const [scores, setScores] = useState<VpgScoreRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [persona, setPersona] = useState('');
  const [kind, setKind] = useState('');
  const [classification, setClassification] = useState('');
  // '' = all, 'yes' = has model recommendations, 'no' = still needs them. A dropdown like
  // the other filters (Adam, 2026-07-31), replacing the old checkbox.
  const [recs, setRecs] = useState<'' | 'yes' | 'no'>('');
  // Dormant = quiet 28+ days. Half the queue. Hidden by default so 213 live conversations
  // are not buried under 1,221 month-old ones; nothing is deleted and All Threads shows all.
  const [age, setAge] = useState<'active' | 'dormant'>('active');
  // Most-recent reply first: the agent who just texted back is still at their phone.
  const [sort, setSort] = useState<'oldest' | 'newest'>('newest');
  const [q, setQ] = useState('');

  const [selected, setSelected] = useState<{ locationId: string; contactId: string } | null>(null);
  const [detail, setDetail] = useState<VpgThreadDetail | null>(null);
  const [detailErr, setDetailErr] = useState<string | null>(null);

  // The vote: which candidate is selected, and the (possibly edited) text that Execute will
  // actually send. `chosen === null` with text present = the operator wrote a fourth option.
  const [chosen, setChosen] = useState<string | null>(null);
  const [draft1, setDraft1] = useState('');
  const [draft2, setDraft2] = useState('');
  const [busy, setBusy] = useState(false);
  const [showScores, setShowScores] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  // Brief post-send state: the reply is painted into the thread and the action panel is
  // replaced by a confirmation, so the send is visibly acknowledged before we advance.
  const [justSent, setJustSent] = useState(false);
  // True while a draft-on-demand request is outstanding for the open thread.
  const [drafting, setDrafting] = useState(false);
  // Replies that never reached the agent. Surfaced as a banner rather than a filter because
  // the whole risk is that nobody goes looking: the thread already left the queue.
  const [issues, setIssues] = useState<VpgDeliveryIssue[] | null>(null);
  const [showIssues, setShowIssues] = useState(false);
  // Which recommendation box 4 was pre-filled from, so an edited candidate still scores
  // against the variant that produced it.
  const [customSource, setCustomSource] = useState<string | null>(null);

  // @block 1.3 Queue load. Re-runs on every filter change.
  const loadQueue = useCallback(() => {
    setErr(null);
    listVpgQueue({
      persona: persona || undefined,
      kind: kind || undefined,
      classification: classification || undefined,
      q: q || undefined,
      ready: recs === '' ? undefined : recs === 'yes',
      dormant: age === 'dormant',
      sort,
      take: 200,
    })
      .then(setRows)
      .catch((e) => setErr(String(e)));
  }, [persona, kind, classification, q, recs, age, sort]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  useEffect(() => {
    getVpgFacets().then(setFacets).catch(() => setFacets(null));
    getVpgScoreboard().then(setScores).catch(() => setScores(null));
    listVpgDeliveryIssues({ days: 7 }).then(setIssues).catch(() => setIssues(null));
  }, []);

  // @block 1.4 Detail load. Pre-selects the first non-stale candidate so the common path
  // (agree with the model, hit Execute) is two clicks, not four.
  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    let live = true;
    setDetail(null); setDetailErr(null); setChosen(null);
    setDraft1(''); setDraft2(''); setRejectReason(''); setCustomSource(null);
    setDrafting(false);
    const { locationId, contactId } = selected;
    let poll: number | undefined;

    getVpgThread(locationId, contactId)
      .then((d) => {
        if (!live) return;
        setDetail(d);
        const fresh = d.candidates.find((c) => !c.stale);
        if (fresh) { pick(fresh); return; }

        // @block 1.4c Draft on demand. No fresh recommendations for this thread, so ask for
        // them now instead of having pre-drafted 2,300 threads nobody will open. Generation
        // is ~26s, which is roughly how long it takes to read the conversation, so by the
        // time the operator has caught up the boxes are usually there. Poll rather than hold
        // the request open; a failed enqueue just means no recs, and box 4 always works.
        if (!d.sendable) return;
        setDrafting(true);
        requestVpgDraft(locationId, contactId).catch(() => {});
        let ticks = 0;
        poll = window.setInterval(async () => {
          ticks += 1;
          try {
            const again = await getVpgThread(locationId, contactId);
            if (!live) return;
            const ready = again.candidates.find((c) => !c.stale);
            if (ready) {
              setDetail(again);
              pick(ready);
              setDrafting(false);
              window.clearInterval(poll);
            }
          } catch { /* transient; keep polling */ }
          // ~2 min ceiling. Past that the worker is wedged or the persona is unmined, and
          // a spinner that never resolves is worse than an honest empty state.
          if (ticks >= 24 && live) { setDrafting(false); window.clearInterval(poll); }
        }, 5000);
      })
      .catch((e) => { if (live) setDetailErr(String(e)); });

    return () => { live = false; if (poll) window.clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.locationId, selected?.contactId]);

  // Selecting a recommendation also pre-fills box 4 from it, so "use this but change one
  // word" is a click plus a keystroke rather than retyping the message.
  function pick(c: VpgCandidate) {
    setChosen(c.variant);
    setDraft1(c.body ?? '');
    setDraft2(c.body2 ?? '');
    setCustomSource(c.variant);
  }

  // @block 1.4b What Execute will actually send. Boxes 1-3 send the stored candidate
  // verbatim; box 4 sends the edited text but keeps the variant it was derived from, so an
  // edited V5 still scores as a V5 that needed work rather than vanishing from the
  // scoreboard. The server recomputes `customized` by diffing against the stored candidate,
  // so this attribution cannot be gamed from the client.
  const outgoing = useMemo(() => {
    if (chosen === 'custom') {
      return { body: draft1, body2: draft2, variant: customSource };
    }
    const c = detail?.candidates.find((x) => x.variant === chosen);
    return { body: c?.body ?? '', body2: c?.body2 ?? '', variant: chosen };
  }, [chosen, draft1, draft2, customSource, detail]);

  const customEdited = useMemo(() => {
    if (!customSource || !detail) return draft1.trim().length > 0;
    const c = detail.candidates.find((x) => x.variant === customSource);
    if (!c) return true;
    return (c.body ?? '').trim() !== draft1.trim() || (c.body2 ?? '').trim() !== draft2.trim();
  }, [customSource, detail, draft1, draft2]);

  const messagesRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (detail && messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [detail]);

  // @block 1.5 Advance to the next thread after a decision, and drop the finished one from
  // the local queue so the list does not show a thread that is no longer open.
  function advance(locationId: string, contactId: string) {
    setRows((prev) => {
      if (!prev) return prev;
      const idx = prev.findIndex((r) => r.locationId === locationId && r.contactId === contactId);
      const next = prev.filter((_, i) => i !== idx);
      const following = next[idx] ?? next[idx - 1] ?? null;
      setSelected(following ? { locationId: following.locationId, contactId: following.contactId } : null);
      return next;
    });
    getVpgScoreboard().then(setScores).catch(() => {});
  }

  // @block 1.5b Execute. ONE click, no confirmation dialog (Adam, 2026-07-31): Patrick works
  // a long queue and a modal on every thread is a tax he pays hundreds of times to catch a
  // mistake he is not making, since the box he is sending is the box he is looking at.
  // The protections that remain are the ones that do not cost a click: the server claims the
  // thread before sending so a double-click cannot double-text, and the queue view has
  // already made opt-outs and DND contacts unreachable.
  async function onExecute() {
    if (!selected || !detail || busy) return;
    const m1 = (outgoing.body ?? '').trim();
    if (!m1) { toast('Pick a box first'); return; }
    const m2 = (outgoing.body2 ?? '').trim() || null;

    setBusy(true);
    try {
      // A 409 "busy" means the server refused to send because the thread table was locked
      // (refresh in flight) and NOTHING was transmitted, so retrying is safe by contract.
      // Post-026 the lock window is milliseconds, so one short retry absorbs the residual
      // collision instead of showing Patrick an error for a condition that self-heals.
      const send = () => executeVpgReply(selected.locationId, selected.contactId, {
        variant: outgoing.variant, message: m1, message2: m2,
      });
      let r;
      try {
        r = await send();
      } catch (e) {
        if (!String(e).includes('"busy"')) throw e;
        toast('Table busy, retrying…');
        await new Promise((res) => setTimeout(res, 2000));
        r = await send();
      }
      const tag = outgoing.variant ?? 'custom';
      toast(r.warning ? `Sent (${r.warning})` : `✓ Sent · ${tag}${r.customized ? ' (edited)' : ''}`);

      // Show the reply landing in the thread before moving on. Patrick reported he could
      // not see what he had just sent: the thread used to vanish the instant Execute
      // returned, and the message itself did not reach raw.ghl_messages until the next
      // sync. The server now writes through on send, and this paints it immediately so the
      // confirmation is the message itself rather than a toast he might miss.
      const now = new Date().toISOString();
      const appended = [
        { id: r.messageIds?.[0] ?? 'sent-1', direction: 'outbound', body: m1, dateAdded: now },
        ...(m2 ? [{ id: r.messageIds?.[1] ?? 'sent-2', direction: 'outbound', body: m2, dateAdded: now }] : []),
      ];
      setDetail((d) => (d ? { ...d, messages: [...d.messages, ...appended] } : d));
      setJustSent(true);
      setTimeout(() => {
        setJustSent(false);
        advance(selected.locationId, selected.contactId);
      }, 2200);
    } catch (e) {
      toast(`Send failed: ${e}`);
      setDetailErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  // Reject is also one click. The reason is an optional inline field above the buttons, not a
  // prompt(): a modal that must be dismissed to skip a thread makes skipping cost more than
  // sending, which is exactly backwards.
  async function onReject(state: 'handled_by_human' | 'dead') {
    if (!selected || busy) return;
    const reason = rejectReason.trim() || undefined;
    setBusy(true);
    try {
      await rejectVpgReply(selected.locationId, selected.contactId, { state, reason });
      toast(state === 'dead' ? 'Marked dead' : 'Rejected, removed from queue');
      advance(selected.locationId, selected.contactId);
    } catch (e) {
      toast(`Failed: ${e}`);
    } finally {
      setBusy(false);
    }
  }

  const personaOptions = facets?.facets.persona ?? [];
  const kindOptions = facets?.facets.kind ?? [];
  const classOptions = facets?.facets.classification ?? [];

  return (
    <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr', height: '100%', minHeight: 0 }}>
      {/* @block 1.6 Filter bar + scoreboard toggle */}
      <div style={{
        padding: `10px ${px}px`, borderBottom: '1px solid var(--t2-border)',
        display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <span style={{ color: 'var(--tx-3)', fontSize: 14 }}>
          {rows == null
            ? 'Loading…'
            : `${rows.length} ${age === 'dormant' ? 'dormant' : 'open'} ${rows.length === 1 ? 'reply' : 'replies'}`}
          {facets && age === 'active' ? ` of ${facets.total} active` : ''}
        </span>

        <select value={persona} onChange={(e) => setPersona(e.target.value)} style={selStyle}>
          <option value="">All personas</option>
          {personaOptions.map((o) => <option key={o.value ?? ''} value={o.value ?? ''}>{o.value} ({o.count})</option>)}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} style={selStyle}>
          <option value="">All kinds</option>
          {kindOptions.map((o) => <option key={o.value ?? ''} value={o.value ?? ''}>{o.value} ({o.count})</option>)}
        </select>
        <select value={classification} onChange={(e) => setClassification(e.target.value)} style={selStyle}>
          <option value="">All classes</option>
          {classOptions.map((o) => <option key={o.value ?? ''} value={o.value ?? ''}>{o.value} ({o.count})</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value as 'oldest' | 'newest')} style={selStyle}>
          <option value="newest">Replied most recently</option>
          <option value="oldest">Longest waiting</option>
        </select>
        <select value={age} onChange={(e) => setAge(e.target.value as typeof age)} style={selStyle}>
          <option value="active">Active (replied within 28d)</option>
          <option value="dormant">Dormant (quiet 28d+)</option>
        </select>
        <select value={recs} onChange={(e) => setRecs(e.target.value as typeof recs)} style={selStyle}>
          <option value="">All threads</option>
          <option value="yes">Has recommendations</option>
          <option value="no">Needs recommendations</option>
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search message or name"
          style={{ ...selStyle, minWidth: 180 }}
        />
        <button onClick={() => setShowScores((v) => !v)} style={btnGhost}>
          {showScores ? '▼' : '▶'} Model scoreboard
        </button>
      </div>

      {/* @block 1.6b Undelivered banner. These threads are already marked sent and gone from
          the queue, so if this is not pushed in front of the operator nobody ever learns the
          agent was never actually contacted. */}
      {issues != null && issues.length > 0 && (
        <div style={{
          padding: `10px ${px}px`, borderBottom: '1px solid var(--t2-border)',
          background: 'rgba(248,113,113,0.10)',
        }}>
          <button
            onClick={() => setShowIssues((v) => !v)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                     color: '#f87171', fontSize: 14, fontWeight: 700 }}
          >
            {showIssues ? '▼' : '▶'} {issues.length} {issues.length === 1 ? 'reply' : 'replies'} never
            reached the agent in the last 7 days
          </button>
          {showIssues && (
            <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
              {issues.map((i) => (
                <div key={i.messageId ?? `${i.contactId}-${i.decidedAt}`}
                     style={{ ...S.pane, padding: '9px 12px' }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx-0)' }}>
                      {i.contactName || i.phone || i.contactId}
                    </span>
                    <span style={{ fontSize: 13, color: '#f87171', fontWeight: 600 }}>{i.issue}</span>
                    <span style={{ fontSize: 13, color: 'var(--tx-3)' }}>{i.market}</span>
                    <span style={{ fontSize: 13, color: 'var(--tx-3)' }}>{fmtTime(i.decidedAt)}</span>
                    {i.phone && <span style={{ fontSize: 13, color: 'var(--tx-2)' }}>{i.phone}</span>}
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--tx-2)', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                    {i.sentBody}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 4 }}>
                    {/* The carrier's own reason beats our generic line: "Landline. Cannot
                        receive SMS." says call them; "blocked due to carrier policies" says
                        the number is fine and the content/reputation is the problem. */}
                    {i.carrierError
                      ? `${i.carrierError} This agent was never contacted.`
                      : i.issue === 'stalled'
                        ? 'Stuck at "sent" in GoHighLevel, never confirmed either way. Worth reaching out another way.'
                        : 'The carrier rejected it. This agent was never contacted.'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showScores && (
        <div style={{ padding: `8px ${px}px`, borderBottom: '1px solid var(--t2-border)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {(scores ?? []).length === 0 && (
            <span style={{ fontSize: 14, color: 'var(--tx-3)', fontStyle: 'italic' }}>
              No decisions recorded yet. Every Execute and Reject scores a variant.
            </span>
          )}
          {(scores ?? []).map((s) => (
            <div key={s.variant} style={{ ...S.pane, padding: '6px 12px', minWidth: 150 }}>
              <div style={{ ...S.label, color: VARIANT_META[s.variant]?.accent ?? 'var(--tx-3)' }}>
                {VARIANT_META[s.variant]?.label ?? s.variant}
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--tx-0)' }}>
                {s.cleanShipPct != null ? `${s.cleanShipPct}%` : '—'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--tx-3)' }}>
                shipped unedited · {s.sent} sent, {s.sentEdited} edited, {s.rejected} rejected
              </div>
            </div>
          ))}
        </div>
      )}

      {/* @block 1.7 Queue + detail */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(280px, 360px) 1fr',
        minHeight: 0,
      }}>
        {/* --- Queue list --- */}
        <div style={{
          overflow: 'auto', borderRight: isMobile ? 'none' : '1px solid var(--t2-border)',
          display: isMobile && selected ? 'none' : 'block',
        }}>
          {err && <div style={{ color: '#f87171', fontSize: 15, padding: 14 }}>{err}</div>}
          {rows == null && !err && (
            <div style={{ color: 'var(--tx-3)', fontStyle: 'italic', padding: 20 }}>Loading queue…</div>
          )}
          {rows?.length === 0 && (
            <div style={{ color: 'var(--tx-3)', fontStyle: 'italic', padding: 20 }}>
              Nothing waiting. Queue is clear.
            </div>
          )}
          {rows?.map((r) => {
            const active = selected?.locationId === r.locationId && selected?.contactId === r.contactId;
            return (
              <button
                key={`${r.locationId}:${r.contactId}`}
                onClick={() => setSelected({ locationId: r.locationId, contactId: r.contactId })}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                  padding: '10px 14px', border: 'none',
                  borderBottom: '1px solid var(--t2-border)',
                  borderLeft: active ? '3px solid var(--brand-3)' : '3px solid transparent',
                  background: active ? 'rgba(20,193,168,0.08)' : 'transparent',
                  color: 'inherit', font: 'inherit',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                  <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--tx-0)' }}>
                    {/* Phone before the raw GHL id: a contact can exist in a thread without
                        a contact record, and a number is something an operator can actually
                        act on where "aN47JgDSsoWwJ5mIDjq6" is noise. */}
                    {r.contactName || r.firstName || r.phone || r.contactId}
                  </span>
                  <span style={{ fontSize: 13, color: waitTone(r.minutesWaiting), fontWeight: 600 }}>
                    {waitLabel(r.minutesWaiting)}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--tx-3)', marginTop: 2, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>{r.market}</span>
                  {r.classification && (
                    <span style={{ color: CLASS_TONE[r.classification] ?? 'var(--tx-3)' }}>· {r.classification}</span>
                  )}
                  {/* Both states get an explicit chip (Adam, 2026-07-31): "ready" threads are
                      a two-click send, "no recs" threads mean the model has not caught up or
                      the persona is unmined, and the operator should know which mode he is in
                      before opening the thread. */}
                  {r.candidateN > 0 ? (
                    <span style={{
                      color: '#04121c', background: '#14C1A8', borderRadius: 3,
                      padding: '1px 7px', fontSize: 11, fontWeight: 700,
                    }}>
                      {r.candidateN} RECS
                    </span>
                  ) : (
                    <span style={{
                      color: '#f59e0b', border: '1px solid rgba(245,158,11,0.45)', borderRadius: 3,
                      padding: '0 6px', fontSize: 11, fontWeight: 700,
                    }}>
                      NO RECS
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize: 14, color: 'var(--tx-2)', marginTop: 4,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {r.lastInboundBody}
                </div>
              </button>
            );
          })}
        </div>

        {/* --- Detail --- */}
        <div style={{ overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {!selected && (
            <div style={{ color: 'var(--tx-3)', fontStyle: 'italic', padding: 30 }}>
              Pick a thread to review its conversation and reply options.
            </div>
          )}
          {selected && detail == null && !detailErr && (
            <div style={{ color: 'var(--tx-3)', fontStyle: 'italic', padding: 30 }}>Loading thread…</div>
          )}
          {detailErr && <div style={{ color: '#f87171', fontSize: 15, padding: 20 }}>{detailErr}</div>}

          {detail && (
            <>
              {/* @block 1.8 Thread header — everything needed to decide, at a glance */}
              <div style={{ padding: `12px ${px}px`, borderBottom: '1px solid var(--t2-border)' }}>
                {isMobile && (
                  <button onClick={() => setSelected(null)} style={{ ...btnGhost, marginBottom: 6 }}>← Queue</button>
                )}
                <div style={{ fontSize: 21, fontWeight: 700, color: 'var(--tx-0)' }}>
                  {detail.thread.contactName || detail.thread.contactId}
                </div>
                <div style={{ fontSize: 14, color: 'var(--tx-3)', marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span>{detail.thread.market}</span>
                  <span>persona {detail.thread.persona ?? '—'}</span>
                  <span>{detail.thread.kind}</span>
                  <span style={{ color: CLASS_TONE[detail.thread.classification ?? ''] ?? 'var(--tx-3)' }}>
                    {detail.thread.classification}
                  </span>
                  <span style={{ color: waitTone(detail.thread.minutesWaiting), fontWeight: 600 }}>
                    waiting {waitLabel(detail.thread.minutesWaiting)}
                  </span>
                  <span>{detail.thread.inboundN} in / {detail.thread.outboundN} out</span>
                </div>
                {(detail.thread.minutesWaiting ?? 0) > 48 * 60 && (
                  <div style={{ marginTop: 6, fontSize: 14, color: '#f87171' }}>
                    Waiting over 48h. This reads as a re-open, not a live answer; a nurture bump usually fits better.
                  </div>
                )}
                {!detail.sendable && (
                  <div style={{ marginTop: 6, fontSize: 14, color: '#f87171', fontWeight: 600 }}>
                    Not sendable: this contact opted out or is DND. Read only.
                  </div>
                )}
              </div>

              {/* @block 1.9 Conversation */}
              <div ref={messagesRef} style={{ flex: 1, overflow: 'auto', padding: `12px ${px}px`, minHeight: 160 }}>
                {detail.messages.map((m, i) => {
                  const out = m.direction === 'outbound';
                  return (
                    <div key={m.id ?? i} style={{ display: 'flex', justifyContent: out ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                      <div style={{
                        maxWidth: '78%', padding: '8px 11px', borderRadius: 8, fontSize: 15, lineHeight: 1.45,
                        background: out ? 'rgba(20,193,168,0.10)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${out ? 'rgba(20,193,168,0.25)' : 'var(--t2-border)'}`,
                        color: 'var(--tx-1)', whiteSpace: 'pre-wrap',
                      }}>
                        {m.body}
                        <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 4 }}>
                          {out ? 'us' : 'agent'} · {fmtTime(m.dateAdded)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {detail.messages.length === 0 && (
                  <div style={{ color: 'var(--tx-3)', fontStyle: 'italic' }}>No SMS history on file for this contact.</div>
                )}
              </div>

              {/* @block 1.10 Four boxes: three recommendations + one editable.
                  Pick a box, hit Execute. No dialog, no intermediate "what will be sent"
                  field: the box you pick is the message, so there is nothing to re-confirm. */}
              {detail.sendable && justSent && (
                <div style={{
                  borderTop: '1px solid var(--t2-border)', padding: `18px ${px}px 22px`,
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <span style={{ fontSize: 21, color: '#14C1A8' }}>✓</span>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx-0)' }}>
                      Sent to {detail.thread.contactName || detail.thread.phone || detail.thread.contactId}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--tx-3)' }}>
                      It is in the thread above. Moving to the next one.
                    </div>
                  </div>
                </div>
              )}

              {detail.sendable && !justSent && (
                <div style={{ borderTop: '1px solid var(--t2-border)', padding: `12px ${px}px 20px` }}>
                  <div style={{ ...S.label, marginBottom: 8 }}>
                    Pick one, then Execute
                    {detail.candidates.length === 0 && (
                      <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: drafting ? '#14C1A8' : 'var(--tx-3)' }}>
                        {drafting
                          ? '  · writing recommendations for this thread, about 30 seconds. You can reply now in box 4.'
                          : '  · no model recommendations for this thread, use box 4'}
                      </span>
                    )}
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(230px, 1fr))',
                    gap: 10, marginBottom: 12, alignItems: 'stretch',
                  }}>
                    {/* Boxes 1-3: the model's recommendations, sent verbatim */}
                    {detail.candidates.map((c) => {
                      const meta = VARIANT_META[c.variant] ?? { label: c.variant, accent: '#7c87a8', blurb: '' };
                      const active = chosen === c.variant;
                      return (
                        <button
                          key={c.variant}
                          onClick={() => pick(c)}
                          style={{
                            textAlign: 'left', cursor: 'pointer', padding: '10px 12px', borderRadius: 6,
                            border: `2px solid ${active ? meta.accent : 'var(--t2-border)'}`,
                            borderLeft: `4px solid ${meta.accent}`,
                            background: active ? `${meta.accent}1f` : 'rgba(10,19,34,0.35)',
                            color: 'inherit', font: 'inherit', opacity: c.stale ? 0.55 : 1,
                          }}
                        >
                          <div style={{ ...S.label, color: meta.accent, marginBottom: 4 }}>
                            {active ? '● ' : '○ '}{meta.label}{c.stale ? ' · stale' : ''}
                          </div>
                          <div style={{ fontSize: 15, color: 'var(--tx-1)', lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>
                            {c.body}
                          </div>
                          {c.body2 && (
                            <div style={{ fontSize: 15, color: 'var(--tx-1)', lineHeight: 1.45, marginTop: 6, whiteSpace: 'pre-wrap' }}>
                              <span style={{ color: 'var(--tx-3)' }}>then: </span>{c.body2}
                            </div>
                          )}
                          <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 6 }}>
                            {c.stale ? 'Written for an earlier message in this thread' : meta.blurb}
                          </div>
                        </button>
                      );
                    })}

                    {/* Box 4: write your own. Pre-filled from whichever box was last picked. */}
                    <div
                      onClick={() => setChosen('custom')}
                      style={{
                        padding: '10px 12px', borderRadius: 6, cursor: 'pointer',
                        border: `2px solid ${chosen === 'custom' ? '#f59e0b' : 'var(--t2-border)'}`,
                        borderLeft: '4px solid #f59e0b',
                        background: chosen === 'custom' ? 'rgba(245,158,11,0.12)' : 'rgba(10,19,34,0.35)',
                      }}
                    >
                      <div style={{ ...S.label, color: '#f59e0b', marginBottom: 4 }}>
                        {chosen === 'custom' ? '● ' : '○ '}Write your own
                        {customEdited && customSource && <span> · edited {customSource}</span>}
                      </div>
                      <textarea
                        value={draft1}
                        onChange={(e) => { setDraft1(e.target.value); setChosen('custom'); }}
                        onFocus={() => setChosen('custom')}
                        rows={3}
                        placeholder="Pick a box above to copy it here and edit, or type a reply from scratch."
                        style={taStyle}
                      />
                      <textarea
                        value={draft2}
                        onChange={(e) => { setDraft2(e.target.value); setChosen('custom'); }}
                        onFocus={() => setChosen('custom')}
                        rows={2}
                        placeholder="Second message (optional). Leave empty to send one text."
                        style={{ ...taStyle, marginTop: 6 }}
                      />
                      <div style={{ fontSize: 12, color: 'var(--tx-3)', marginTop: 6 }}>
                        Sent as a {customSource ? `${customSource} edit` : 'custom reply'}
                        {' · '}{draft1.trim().length}{draft2.trim() ? ` + ${draft2.trim().length}` : ''} chars
                      </div>
                    </div>
                  </div>

                  <input
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Optional note (recorded on reject / mark dead)"
                    style={{ ...selStyle, width: '100%', maxWidth: 520, marginBottom: 10 }}
                  />

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      onClick={onExecute}
                      disabled={busy || !(outgoing.body ?? '').trim()}
                      style={btnPrimary(busy || !(outgoing.body ?? '').trim())}
                    >
                      {busy ? 'Sending…' : (outgoing.body2 ?? '').trim() ? 'Execute (2 texts)' : 'Execute'}
                    </button>
                    <button onClick={() => onReject('handled_by_human')} disabled={busy} style={btnGhost}>
                      Reject all
                    </button>
                    <button onClick={() => onReject('dead')} disabled={busy} style={btnGhost}>
                      Mark dead
                    </button>
                    <span style={{ fontSize: 13, color: 'var(--tx-3)' }}>
                      {chosen
                        ? `sending ${chosen === 'custom' ? (customSource ? `${customSource} (edited)` : 'custom') : chosen}`
                        : 'nothing selected'}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// @subsection 1.11 Local styles (inline, matching the v5 canvas used across Atlas routes)
const selStyle: React.CSSProperties = {
  background: 'rgba(10,19,34,0.6)', color: 'var(--tx-1)',
  border: '1px solid var(--t2-border)', borderRadius: 4,
  padding: '5px 8px', fontSize: 14,
};

const taStyle: React.CSSProperties = {
  width: '100%', background: 'rgba(10,19,34,0.6)', color: 'var(--tx-0)',
  border: '1px solid var(--t2-border)', borderRadius: 4,
  padding: '9px 11px', fontSize: 15, lineHeight: 1.5, resize: 'vertical',
  fontFamily: 'inherit',
};

const btnGhost: React.CSSProperties = {
  background: 'none', border: '1px solid var(--t2-border)', borderRadius: 4,
  padding: '6px 12px', fontSize: 14, fontWeight: 600,
  color: 'var(--tx-2)', cursor: 'pointer',
};

const btnPrimary = (disabled: boolean): React.CSSProperties => ({
  background: disabled ? 'rgba(20,193,168,0.25)' : '#14C1A8',
  border: 'none', borderRadius: 4, padding: '8px 20px',
  fontSize: 15, fontWeight: 700, color: disabled ? 'var(--tx-3)' : '#04121c',
  cursor: disabled ? 'not-allowed' : 'pointer',
});
